import {ItemResponsePublisherBeanObject, ListResponsePublisherBean, PublisherBean, PublisherControllerApi, DomainCategoryControllerApi} from 'src/api/api';
import {BaseApi, configuration} from 'src/api/BaseApi';
import {processError} from 'src/api/customFetch';

class PublisherApi extends BaseApi {
    api: PublisherControllerApi;
    domainCategoryApi: DomainCategoryControllerApi;

    constructor() {
        super();
        this.api = new PublisherControllerApi(configuration, configuration.basePath, this.customFetch);
        this.domainCategoryApi = new DomainCategoryControllerApi(configuration, configuration.basePath, this.customFetch);
    }

    async list(
        size: number = 20,
        page: number = 1,
        params?: { search?: string; searchType?: string }
    ): Promise<ListResponsePublisherBean> {
        try {
            const search = params?.search;
            const searchType = params?.searchType;
            const options: any = {query: {size, page}};
            if (searchType) {
                options.query.searchType = searchType;
            }
            let list = await this.api.publisherList(search, options);
            return Promise.resolve(list)
        } catch (err) {
            return processError(err);
        }
    }

    async all(): Promise<ListResponsePublisherBean> {
        try {
            let all = await this.api.publisherList();
            return Promise.resolve(all)
        } catch (err) {
            return processError(err);
        }
    }

    async view(id): Promise<ItemResponsePublisherBeanObject> {
        try {
            let bean = await this.api.publisher(id);
            return Promise.resolve(bean)
        } catch (err) {
            return processError(err);
        }
    }

    async create(bean: PublisherBean): Promise<ItemResponsePublisherBeanObject> {
        try {
            let created = await this.api.createPublisher(bean);
            return Promise.resolve(created)
        } catch (err) {
            return processError(err);
        }
    }

    async edit(id, bean: PublisherBean): Promise<ItemResponsePublisherBeanObject> {
        try {
            let edited = await this.api.editPublisher(bean, id);
            return Promise.resolve(edited)
        } catch (err) {
            return processError(err);
        }
    }

    async delete(id): Promise<void> {
        try {
            await this.api.deletePublisher(id);
            return Promise.resolve()
        } catch (err) {
            return processError(err);
        }
    }

     async downloadTxtFile(publisherId: number): Promise<string> {
        try {
            const response = await this.api.downloadTxtFile(publisherId);

            return response;
        } catch (err) {
            console.error('Error fetching text file:', err);
            return processError(err);
        }
    }

    async downloadAppAdsTxtFile(publisherId: number): Promise<string> {
        try {
            const response = await this.api.downloadAppAdsTxtFile(publisherId);
            return response;
        } catch (err) {
            console.error('Error fetching app-ads text file:', err);
            const resp = err as any;
    
            // BE returns plain text error (e.g. 400 with string body)
            if (resp && typeof resp.text === 'function') {
                try {
                    const errorText = await resp.text();
                    return Promise.reject({ error: errorText });
                } catch {
                    // fall through to processError if text() also fails
                }
            }
    
            return processError(err);
        }
    }

    async generateToken(id: number): Promise<ItemResponsePublisherBeanObject> {
        try {
            let bean = await this.api.updateToken(id);
            return Promise.resolve(bean)
        } catch (err) {
            return processError(err);
        }
    }
    async activate(id: number): Promise<ItemResponsePublisherBeanObject> {
        try {
            return await this.api.activatePublisher(id);
        } catch (err) {
            return processError(err);
        }
    }
    
    async deactivate(id: number): Promise<ItemResponsePublisherBeanObject> {
        try {
            return await this.api.deactivatePublisher(id);
        } catch (err) {
            return processError(err);
        }
    }

    async searchDomainCategories(search: string = ''): Promise<any> {
        try {
            const result = await this.domainCategoryApi.searchDomainCategories(search);
            return Promise.resolve(result);
        } catch (err) {
            return processError(err);
        }
    }

    async createDomainCategory(body: any): Promise<any> {
        try {
            const result = await this.domainCategoryApi.createDomainCategory(body);
            return Promise.resolve(result);
        } catch (err) {
            return processError(err);
        }
    }

    async updatePaymentDetails(publisherId: number, payload: any): Promise<any> {
        try {
            return await this.api.updatePaymentDetails(payload, publisherId);
        } catch (err) {
            return processError(err);
        }
    }

    async getPaymentDetails(publisherId: number): Promise<any> {
        try {
            const url = configuration.basePath + `/api/publisher/${publisherId}/payment-details`
            const options: RequestInit = {
                method: 'GET',
            }

            return this.customFetch(url, options)
                .then(async (resp) => resp.json().catch(() => null))
                .catch((err) => processError(err))
        } catch (err) {
            return processError(err);
        }
    }

    async getTransactionStartDate(publisherId: number): Promise<any> {
        try {
            return await this.api.transactionStartDate(publisherId);
        } catch (err) {
            return processError(err);
        }
    }

}


const publisherApi = new PublisherApi();
export default publisherApi;
